import React from 'react'
import Button from './Button'

/**
 * "Is this you?" — the confirmation step in front of the EIN field (#62).
 *
 * `funder_grants` already knows ~421k recipient organizations by name, state
 * and EIN, so for a large share of signups we can OFFER the EIN instead of
 * asking someone to go and find nine digits. Measured end to end that is ~59%
 * of organizations — 90% of those already carrying an EIN in our data, times
 * the ~65% that carry one at all — and the rest fall through to the optional
 * field below, unchanged.
 *
 * ## Three rules, and they are the whole design
 *
 * 1. **The human confirms. Always.** Nothing here writes to the form until
 *    someone clicks. A fuzzy name match used for silent attribution is the
 *    2026-07-27 hazard `backend/peer_evidence.py` exists to prevent; a
 *    suggestion someone says yes to is the safe form of the same operation.
 * 2. **Never preselect an ambiguous name.** 5.22% of normalized names map to
 *    more than one EIN, and the two organizations both filed as "Hearth Inc"
 *    are indistinguishable to us. So a chooser opens with NOTHING chosen — no
 *    default radio, no highlighted row — and the reader picks or walks away.
 * 3. **"None of these" is always reachable**, and choosing it costs nothing:
 *    the optional EIN field is still there, exactly as it was before this
 *    component existed. Onboarding stays under 5 minutes.
 *
 * Every card links the organization's actual filing on ProPublica. That link is
 * the cheapest defence there is against the expensive error — a wrong EIN
 * silently attributes another organization's grant history to this client — and
 * it costs us nothing to show.
 *
 * Deliberately NOT a modal and not a blocking step: it renders inline, above
 * the EIN field, and ignoring it entirely is a valid way to use the product.
 *
 * ⚠️ **This no longer runs at signup.** `GET /api/org-lookup` requires a
 * confirmed account (ADR-0018), so the wizard cannot call it; the caller is
 * `OrgIdentitySetup`, on the dashboard, after confirmation (#65). The "under 5
 * minutes" constraint in rule 3 still applies — it just applies to a card the
 * client can ignore rather than to a step in a form.
 */

function Location({ city, state }) {
  const place = [city, state].filter(Boolean).join(' ')
  return place ? <span className="text-gray-600">{place}</span> : null
}

/** One organization, with everything a human needs to say yes or no. */
function OrgLine({ org }) {
  return (
    <>
      <span className="font-medium text-gray-900">{org.name}</span>
      {(org.city || org.state) && <>{' · '}<Location city={org.city} state={org.state} /></>}
      <span className="text-gray-600">{' · EIN '}{org.ein}</span>
    </>
  )
}

/**
 * What the reader currently has, stated plainly next to what we are offering.
 *
 * In correction mode a suggestion is, by construction, a DIFFERENT EIN from the
 * stored one: if the stored EIN appeared in any filing we hold, the caller would
 * never be in the "no filing names you" state that renders this. So the claim is
 * narrow and checkable — not "yours is wrong", but "yours appears nowhere in our
 * filings, and this organization's does."
 */
function CurrentEinNote({ currentEin }) {
  return (
    <p className="text-xs text-gray-500 mt-1">
      Your account uses EIN <span className="font-medium text-gray-700">{currentEin}</span>,
      which appears in no 990 we have imported.
    </p>
  )
}

function CheckLink({ org }) {
  if (!org.ein_url) return null
  return (
    <a
      href={org.ein_url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-brand-500 hover:underline"
    >
      Check this organization’s filings ↗
    </a>
  )
}

export default function OrgIdentityConfirm({
  state,           // 'idle' | 'loading' | 'match' | 'choose' | 'confirmed'
  suggestions = [],
  confirmed = null,
  onConfirm,       // (org) => void  — the ONLY path that sets the EIN
  onReject,        // ()   => void
  // The caller is persisting a choice. In the wizard this was instant (it only
  // set form state); from the dashboard it is a PATCH, so the click needs to
  // show it landed. Rejecting is disabled too — the write is already in flight.
  busy = false,
  // CORRECTION mode: the client already has an EIN and it appears in no filing
  // we hold. Set it and the copy stops asking "is this you?" and starts asking
  // "is this you INSTEAD?", because confirming now OVERWRITES a value rather
  // than filling a blank — a worse failure if it is wrong, so the reader is
  // shown what they currently have and "mine is right" is a first-class answer.
  currentEin = null,
}) {
  if (state === 'idle') return null

  if (state === 'loading') {
    return (
      <p className="text-xs text-gray-500" aria-live="polite">
        Looking for your organization in IRS grant filings…
      </p>
    )
  }

  if (state === 'confirmed' && confirmed) {
    return (
      <div
        className="rounded-xl border border-brand-300 bg-brand-100 px-4 py-3"
        aria-live="polite"
      >
        <p className="text-sm text-brand-900">
          <span aria-hidden="true">✓ </span>
          Using EIN <span className="font-semibold">{confirmed.ein}</span> for{' '}
          <span className="font-medium">{confirmed.name}</span>.
        </p>
        <div className="flex items-center gap-3 mt-1">
          <button
            type="button"
            onClick={onReject}
            className="text-xs text-brand-500 hover:underline"
          >
            That’s not us, clear it
          </button>
          <CheckLink org={confirmed} />
        </div>
      </div>
    )
  }

  if (state === 'match') {
    const org = suggestions[0]
    if (!org) return null
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3" aria-live="polite">
        <p className="text-sm font-medium text-gray-900">
          {currentEin ? 'Is this you instead?' : 'Is this you?'}
        </p>
        {currentEin && <CurrentEinNote currentEin={currentEin} />}
        <p className="text-sm mt-1"><OrgLine org={org} /></p>
        <p className="text-xs text-gray-500 mt-1">
          Found in {org.funder_count === 1 ? '1 foundation’s' : `${org.funder_count} foundations’`}{' '}
          IRS filings.{' '}
          {currentEin
            ? 'Switching to it would let us tell you which foundations have already funded you.'
            : 'Confirming it lets us tell you which foundations have already funded you.'}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {/* The only path that sets the EIN. */}
          <Button size="sm" loading={busy} onClick={() => onConfirm(org)}>
            {currentEin ? 'Yes, use this one' : 'Yes, that’s us'}
          </Button>
          <button
            type="button"
            onClick={onReject}
            disabled={busy}
            className="text-sm text-gray-600 hover:underline disabled:opacity-50"
          >
            {currentEin ? 'No, mine is right' : 'No, that’s not us'}
          </button>
          <CheckLink org={org} />
        </div>
      </div>
    )
  }

  // state === 'choose' — more than one organization files under this name.
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3" aria-live="polite">
      <p className="text-sm font-medium text-gray-900">
        {suggestions.length} organizations file under that name. Which one are you?
      </p>
      {currentEin && <CurrentEinNote currentEin={currentEin} />}
      <p className="text-xs text-gray-500 mt-1">
        We won’t guess, because picking the wrong one would attach another organization’s
        grant history to your account.
      </p>
      <ul className="mt-3 space-y-2">
        {suggestions.map(org => (
          <li
            key={org.ein}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg
              border border-gray-200 bg-white px-3 py-2"
          >
            <span className="text-sm"><OrgLine org={org} /></span>
            <span className="flex items-center gap-3">
              <CheckLink org={org} />
              <Button
                size="sm"
                variant="secondary"
                loading={busy}
                onClick={() => onConfirm(org)}
              >
                This is us
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onReject}
        disabled={busy}
        className="text-sm text-gray-600 hover:underline mt-3 disabled:opacity-50"
      >
        {currentEin ? 'None of these, mine is right' : 'None of these'}
      </button>
    </div>
  )
}
